import React, { useState, useEffect, useCallback } from 'react';
import { Container, Card, Button, Form, Row, Col, Badge, Alert, Spinner } from 'react-bootstrap';
import { FaArrowLeft, FaPlus, FaPaperPlane } from 'react-icons/fa';
import api from '../api/axios';
import { formatDateTime } from '../utils/format';
import SupportThread from '../components/support/SupportThread';
import { CATEGORIES, categoryMeta, statusMeta, hasPhone } from '../components/support/supportMeta';
import '../components/support/support.css';

const EMPTY_FORM = { category: 'bug', title: '', body: '' };

// 번호 패턴이 보일 때 등록 버튼을 한 번 가로막고 띄우는 경고.
//
// 차단이 아니라 되묻기인 이유: 본문의 번호가 **사장님 본인 것**인지 손님 것인지는
// 정규식으로 구분이 안 된다("제 번호 010-... 로 연락주세요" 는 적법하고 흔하다).
// 기계가 못 하는 구분을 1초에 하는 사람이 화면 앞에 있으니 그 판단을 맡긴다.
// 막아버리면 전화·카톡으로 돌아가서 우리 쪽 기록만 사라진다.
const PhoneWarning = ({ show }) => {
    if (!show) return null;
    return (
        <Alert variant="warning" className="mt-2 mb-0 sp-meta" style={{ wordBreak: 'keep-all' }}>
            휴대폰 번호가 들어 있는 것 같습니다. <strong>손님 연락처라면 지워주세요</strong> —
            차량번호나 작업 번호로 찾을 수 있습니다. 사장님 본인 번호라면 그대로 다시 눌러 등록하세요.
        </Alert>
    );
};

// 사장님용 고객센터 — 목록 / 새 문의 / 스레드 세 모드를 한 페이지에서 바꾼다.
// 라우트를 쪼개지 않은 이유: 문의 건수가 매장당 한 자릿수라 목록이 '집' 역할을 못 한다.
const SupportPage = () => {
    const [view, setView] = useState('list'); // 'list' | 'new' | 'detail'
    const [tickets, setTickets] = useState([]);
    const [detail, setDetail] = useState(null); // { ticket, messages }
    const [form, setForm] = useState(EMPTY_FORM);
    const [reply, setReply] = useState('');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    // 번호 경고를 한 번 보고도 보내려는 경우 — 두 번째 클릭은 통과시킨다.
    // 사장님 본인 번호를 적는 건 적법하고 흔하다. 차단하면 전화·카톡으로 돌아가
    // 우리 쪽 기록만 사라지므로, 막지 않고 한 번만 되묻는다.
    const [phoneWarned, setPhoneWarned] = useState(false);

    const fetchList = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.get('/support/tickets');
            setTickets(res.data.data?.items || []);
        } catch (err) {
            setError(err.response?.data?.error || '문의 목록을 불러오지 못했습니다.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchList(); }, [fetchList]);

    const openDetail = async (id) => {
        setError('');
        setSuccess('');
        setLoading(true);
        try {
            const res = await api.get(`/support/tickets/${id}`);
            setDetail(res.data.data);
            setReply('');
            setView('detail');
        } catch (err) {
            setError(err.response?.data?.error || '문의를 불러오지 못했습니다.');
        } finally {
            setLoading(false);
        }
    };

    const backToList = () => {
        setView('list');
        setDetail(null);
        setError('');
        // 목록을 다시 읽어 읽음 표시·상태를 반영한다.
        fetchList();
    };

    const handleCreate = async (e) => {
        e.preventDefault();
        if (!form.title.trim() || !form.body.trim()) {
            setError('제목과 내용을 입력해주세요.');
            return;
        }
        if (!phoneWarned && (hasPhone(form.body) || hasPhone(form.title))) {
            setPhoneWarned(true);
            return;
        }
        setSaving(true);
        setError('');
        try {
            const res = await api.post('/support/tickets', {
                category: form.category,
                title: form.title.trim(),
                body: form.body.trim(),
            });
            setForm(EMPTY_FORM);
            setPhoneWarned(false);
            setSuccess('문의가 등록되었습니다. 답변이 등록되면 이 페이지에서 확인할 수 있습니다.');
            await fetchList();
            await openDetail(res.data.data.id);
        } catch (err) {
            setError(err.response?.data?.error || '문의 등록에 실패했습니다.');
        } finally {
            setSaving(false);
        }
    };

    const handleReply = async (e) => {
        e.preventDefault();
        if (!reply.trim()) return;
        if (!phoneWarned && hasPhone(reply)) {
            setPhoneWarned(true);
            return;
        }
        setSaving(true);
        setError('');
        try {
            await api.post(`/support/tickets/${detail.ticket.id}/messages`, { body: reply.trim() });
            setReply('');
            setPhoneWarned(false);
            await openDetail(detail.ticket.id);
        } catch (err) {
            setError(err.response?.data?.error || '등록에 실패했습니다.');
        } finally {
            setSaving(false);
        }
    };

    // ── 새 문의 ──
    if (view === 'new') {
        return (
            <Container className="py-4" style={{ maxWidth: 800 }}>
                <Button variant="link" className="ps-0 mb-2" onClick={() => { setView('list'); setError(''); }}>
                    <FaArrowLeft className="me-1" /> 목록으로
                </Button>
                <Card>
                    <Card.Header><strong>새 문의 작성</strong></Card.Header>
                    <Card.Body>
                        {error && <Alert variant="danger">{error}</Alert>}
                        <Form onSubmit={handleCreate}>
                            <Row className="g-3">
                                <Col xs={12} sm={5}>
                                    <Form.Label>분류</Form.Label>
                                    <Form.Select value={form.category}
                                        onChange={e => setForm({ ...form, category: e.target.value })}>
                                        {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                                    </Form.Select>
                                </Col>
                                <Col xs={12}>
                                    <Form.Label>제목</Form.Label>
                                    <Form.Control value={form.title} maxLength={200}
                                        placeholder="예) 완료 처리할 때 금액이 0원으로 저장됩니다"
                                        onChange={e => setForm({ ...form, title: e.target.value })} />
                                </Col>
                                <Col xs={12}>
                                    <Form.Label>내용</Form.Label>
                                    {/* 안내를 입력란 **위**로 올렸다. 아래(글자 수 옆)에 두면 쓰기를 끝낸
                                        뒤에야 보이고, 그때는 이미 적어버린 상태라 늦다. 손님 정보가 애초에
                                        안 들어오게 막는 게 유일하게 보유량 자체를 줄이는 수단이다 —
                                        본문은 자유 입력이라 감사 로그처럼 키 이름으로 거를 수가 없다. */}
                                    <div className="sp-pii-note" style={{ wordBreak: 'keep-all' }}>
                                        손님 이름·연락처는 적지 말아주세요. <strong>차량번호나 작업 번호</strong>로 알려주시면 찾을 수 있습니다.
                                    </div>
                                    <Form.Control as="textarea" rows={8} value={form.body} maxLength={4000}
                                        placeholder={'어떤 화면에서, 어떤 순서로 하다가 생긴 일인지 적어주시면 훨씬 빨리 확인할 수 있습니다.\n사진이 필요한 내용이면 문의를 남기신 뒤 알려주세요.'}
                                        onChange={e => setForm({ ...form, body: e.target.value })} />
                                    <Form.Text className="text-muted d-block">{form.body.length} / 4000자</Form.Text>
                                    <PhoneWarning show={phoneWarned} />
                                </Col>
                                <Col xs={12} className="text-end">
                                    <Button type="submit" variant="primary" disabled={saving} className="text-nowrap">
                                        {saving ? '등록 중...' : '문의 등록'}
                                    </Button>
                                </Col>
                            </Row>
                        </Form>
                    </Card.Body>
                </Card>
            </Container>
        );
    }

    // ── 스레드 ──
    if (view === 'detail' && detail) {
        const { ticket, messages } = detail;
        const cat = categoryMeta(ticket.category);
        const st = statusMeta(ticket.status);
        return (
            <Container className="py-4" style={{ maxWidth: 800 }}>
                <Button variant="link" className="ps-0 mb-2" onClick={backToList}>
                    <FaArrowLeft className="me-1" /> 목록으로
                </Button>
                <Card>
                    <Card.Header>
                        <div className="d-flex flex-wrap align-items-center gap-2 mb-1">
                            <Badge bg={cat.variant}>{cat.label}</Badge>
                            <Badge bg={st.variant}>{st.label}</Badge>
                            <span className="sp-meta ms-auto">{formatDateTime(ticket.created_at)}</span>
                        </div>
                        <div className="sp-list-title">{ticket.title}</div>
                    </Card.Header>
                    <Card.Body>
                        {error && <Alert variant="danger">{error}</Alert>}
                        <SupportThread messages={messages} mySide="shop" />
                    </Card.Body>
                    {ticket.status === 'closed' ? (
                        <Card.Footer className="sp-meta">
                            종료된 문의입니다. 이어서 물어보실 내용이 있으면 새 문의로 남겨주세요.
                        </Card.Footer>
                    ) : (
                        <Card.Footer>
                            <Form onSubmit={handleReply}>
                                <Form.Control as="textarea" rows={3} value={reply} maxLength={4000}
                                    placeholder="추가로 남길 내용을 적어주세요"
                                    onChange={e => setReply(e.target.value)} />
                                <PhoneWarning show={phoneWarned} />
                                <div className="text-end mt-2">
                                    <Button type="submit" variant="primary" disabled={saving || !reply.trim()} className="text-nowrap">
                                        <FaPaperPlane className="me-1" />{saving ? '등록 중...' : '보내기'}
                                    </Button>
                                </div>
                            </Form>
                        </Card.Footer>
                    )}
                </Card>
            </Container>
        );
    }

    // ── 목록 ──
    return (
        <Container className="py-4" style={{ maxWidth: 800 }}>
            <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
                <h2 className="mb-0" style={{ fontSize: '1.5rem' }}>고객센터</h2>
                <Button variant="primary" className="ms-auto text-nowrap"
                    onClick={() => { setView('new'); setError(''); setSuccess(''); }}>
                    <FaPlus className="me-1" /> 새 문의
                </Button>
            </div>
            <p className="sp-meta">
                버그 제보 · 개선 요청 · 건의 · 결제 문의를 남기시면 운영팀이 확인해 답변해 드립니다.
            </p>

            {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}
            {success && <Alert variant="success" onClose={() => setSuccess('')} dismissible>{success}</Alert>}

            {loading ? (
                <div className="text-center py-5"><Spinner animation="border" /></div>
            ) : tickets.length === 0 ? (
                <Card><Card.Body className="text-center text-muted py-5">
                    아직 남긴 문의가 없습니다.
                </Card.Body></Card>
            ) : (
                <div className="d-flex flex-column gap-2">
                    {tickets.map(t => {
                        const cat = categoryMeta(t.category);
                        const st = statusMeta(t.status);
                        return (
                            // Card 를 버튼으로 쓰면 모바일 전역 min-height:44px 와 싸우게 되므로
                            // role/onClick 대신 안쪽 전체를 덮는 투명 버튼 없이 Card 자체에 핸들러를 건다.
                            <Card key={t.id} className={t.shop_unread ? 'sp-row-unread' : ''}
                                style={{ cursor: 'pointer' }} onClick={() => openDetail(t.id)}>
                                <Card.Body className="py-3">
                                    <div className="d-flex flex-wrap align-items-center gap-2 mb-1">
                                        <Badge bg={cat.variant}>{cat.label}</Badge>
                                        <Badge bg={st.variant}>{st.label}</Badge>
                                        {t.shop_unread ? <Badge bg="primary">새 답변</Badge> : null}
                                        <span className="sp-meta ms-auto">{formatDateTime(t.last_reply_at || t.created_at)}</span>
                                    </div>
                                    <div className="sp-list-title">{t.title}</div>
                                    <div className="sp-meta">글 {t.message_count}개</div>
                                </Card.Body>
                            </Card>
                        );
                    })}
                </div>
            )}
        </Container>
    );
};

export default SupportPage;
